// =============================================================================
// CustomerCreateForm — shared inline form for creating a new customer
// =============================================================================
// Used by both the New Order flow and the New Quotation flow. Captures
// everything we need for invoicing + follow-ups in a single card:
//   - Individual vs Company toggle
//   - Name / phone / email / NIC / DOB (individuals only)
//   - Company name / VAT / BRN / contact person (companies)
//   - Map-pinnable address (with Nominatim autocomplete)
//   - Auto-suggested delivery region (from city)
//   - VAT-excluded toggle
//
// Pass `initialPhone` to pre-fill phone (e.g. from a failed search). The
// `onCreated` callback fires with the new Customer record once the POST
// returns; the caller usually closes the form + auto-selects it.

import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { CustomerType, Region } from '@kalachand/shared';
import { apiGet, apiPatch, apiPost, getApiError } from '../../lib/api';
import { useSubmitGuard } from '../../lib/useSubmitGuard';
import { Card } from '../../components/Card';
import { Input } from '../../components/Input';
import { Button } from '../../components/Button';
import { LocationPicker } from '../../components/LocationPicker';
import { AddressAutocomplete } from '../../components/AddressAutocomplete';
import { useHasPermission } from '../../lib/permissions';
import { cn } from '../../lib/cn';

export interface CreatedCustomer {
  id: string;
  customerCode: string;
  type: CustomerType;
  name: string;
  phone: string;
  altPhones: string[];
  email: string | null;
  companyName: string | null;
  vatNumber: string | null;
  brn: string | null;
  contactPerson: string | null;
  vatExcluded: boolean;
  address: string | null;
  addressPrecise: string | null;
  city: string | null;
  country: string | null;
  region: Region | null;
  nationalId: string | null;
  dateOfBirth: string | null;
  notes: string | null;
  preferredShopId: string | null;
  preferredShop?: { id: string; code: string; name: string } | null;
}

// A customer record we can edit (subset of the full record). Optional fields
// pre-fill when available; absent ones just start blank.
export interface EditableCustomer {
  id: string;
  type: CustomerType;
  name: string;
  phone: string;
  altPhones?: string[];
  email: string | null;
  companyName: string | null;
  vatNumber: string | null;
  brn: string | null;
  contactPerson: string | null;
  vatExcluded: boolean;
  address: string | null;
  addressPrecise?: string | null;
  city: string | null;
  region: Region | null;
  nationalId?: string | null;
  dateOfBirth?: string | null;
  latitude?: number | null;
  longitude?: number | null;
}

interface Props {
  onCreated: (c: CreatedCustomer) => void;
  onCancel: () => void;
  /** Pre-fill the phone field — e.g. from a failed search term that looked like a number. */
  initialPhone?: string;
  /** When provided, the form EDITS this customer (PATCH) instead of creating one. */
  customer?: EditableCustomer;
  /** Label for the secondary action (e.g. "Change customer" in the order flow). */
  cancelLabel?: string;
}

// Phones are stored space-free with only digits, a leading "+" and "-" allowed
// (mirrors the shared zod phoneSchema). Whitespace is stripped as the user
// types, but anything else ("/", letters…) is KEPT so validation rejects it
// visibly — a pasted "57939155/57961023" must error out, not get silently
// glued into one 16-digit number. The second number goes in its own
// "Additional number" field instead.
function sanitizePhone(v: string): string {
  return v.replace(/\s+/g, '');
}
function phoneLooksValid(v: string): boolean {
  return /^\+?\d[\d-]*\d$/.test(v) && v.length >= 4;
}
function phoneError(v: string): string | undefined {
  if (!v || phoneLooksValid(v)) return undefined;
  return /[/,;]/.test(v)
    ? 'One number per field — put the second number under “Add another number”.'
    : 'Digits, “+” and “-” only.';
}
// Legacy records hold "57939155/57961023"-style multi-number strings. Split
// them on open so the first becomes the primary and the rest land in the
// additional-numbers list — instead of sanitizing into one mangled number.
function splitLegacyPhones(raw: string): string[] {
  return raw
    .split(/[/,;]+/)
    .map(sanitizePhone)
    .filter(Boolean);
}

export function CustomerCreateForm({
  onCreated,
  onCancel,
  initialPhone = '',
  customer,
  cancelLabel = 'Cancel',
}: Props): JSX.Element {
  const isEdit = !!customer;
  // BRN + VAT number are captured freely at creation, but amending them on an
  // existing record is operations-and-above only (mirrors the backend gate).
  const canEditTaxIds = useHasPermission('customers.edit_tax_ids');
  const taxIdsLocked = isEdit && !canEditTaxIds;
  const [type, setType] = useState<CustomerType>(customer?.type ?? 'individual');
  const [name, setName] = useState(customer?.name ?? '');
  const initialParts = splitLegacyPhones(customer?.phone ?? initialPhone);
  const [phone, setPhone] = useState(initialParts[0] ?? '');
  const [altPhones, setAltPhones] = useState<string[]>([
    ...new Set([...initialParts.slice(1), ...(customer?.altPhones ?? [])]),
  ]);
  const [email, setEmail] = useState(customer?.email ?? '');
  const [address, setAddress] = useState(customer?.address ?? '');
  const [addressPrecise, setAddressPrecise] = useState(customer?.addressPrecise ?? '');
  const [city, setCity] = useState(customer?.city ?? '');
  const [latitude, setLatitude] = useState<number | null>(customer?.latitude ?? null);
  const [longitude, setLongitude] = useState<number | null>(customer?.longitude ?? null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [region, setRegion] = useState<Region | ''>(customer?.region ?? '');
  const [regionAutoSuggested, setRegionAutoSuggested] = useState(false);
  const [companyName, setCompanyName] = useState(customer?.companyName ?? '');
  const [vatNumber, setVatNumber] = useState(customer?.vatNumber ?? '');
  const [brn, setBrn] = useState(customer?.brn ?? '');
  const [contactPerson, setContactPerson] = useState(customer?.contactPerson ?? '');
  const [nationalId, setNationalId] = useState(customer?.nationalId ?? '');
  const [dateOfBirth, setDateOfBirth] = useState(
    customer?.dateOfBirth ? customer.dateOfBirth.slice(0, 10) : '',
  );
  const [vatExcluded, setVatExcluded] = useState(customer?.vatExcluded ?? false);

  // Debounced city → region suggest. Only fires when sales girl hasn't manually
  // picked a region yet (so we don't clobber an explicit choice).
  useEffect(() => {
    if (!city.trim() || (region && !regionAutoSuggested)) return;
    const t = setTimeout(async () => {
      try {
        const res = await apiGet<{ region: Region | null }>('/scheduling/suggest-region', { city });
        if (res.region) {
          setRegion(res.region);
          setRegionAutoSuggested(true);
        }
      } catch {
        // silent — best effort
      }
    }, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [city]);

  const qc = useQueryClient();
  // Synchronous re-entry lock: `create.isPending` only flips after a re-render,
  // so a fast double-click on "Create customer" could fire two POSTs and create
  // the customer twice. The guard blocks the second click in the same tick.
  const guard = useSubmitGuard();
  const create = useMutation({
    mutationFn: () => {
      const body = {
        type,
        name: type === 'company' ? contactPerson || name : name,
        phone,
        altPhones: altPhones.map(sanitizePhone).filter(Boolean),
        email: email || null,
        address: address || null,
        addressPrecise: addressPrecise.trim() || null,
        city: city || null,
        country: 'Mauritius',
        latitude: latitude ?? undefined,
        longitude: longitude ?? undefined,
        region: region || null,
        companyName: type === 'company' ? companyName : null,
        vatNumber: type === 'company' ? vatNumber || null : null,
        brn: type === 'company' ? brn || null : null,
        contactPerson: type === 'company' ? contactPerson || name || null : null,
        nationalId: nationalId || null,
        dateOfBirth: type === 'individual' && dateOfBirth ? dateOfBirth : null,
        vatExcluded,
        preferredChannel: 'whatsapp',
      };
      return isEdit
        ? apiPatch<CreatedCustomer>(`/customers/${customer!.id}`, body)
        : apiPost<CreatedCustomer>('/customers', body);
    },
    onSuccess: (c) => {
      qc.invalidateQueries({ queryKey: ['customer-search'] });
      qc.invalidateQueries({ queryKey: ['customer-search-quote'] });
      // The standalone Customers page query uses ['customers', ...] — refresh
      // it so newly-created customers appear without a hard reload.
      qc.invalidateQueries({ queryKey: ['customers'] });
      onCreated(c);
    },
    onSettled: guard.release,
  });
  const submit = (): void => guard.run(() => create.mutate());

  // A duplicate_phone 409 carries the existing owner (see customers.controller).
  // When present we swap the red error for a recovery callout that lets the user
  // adopt that customer instead of being stuck between "no match" and "exists".
  const apiErr = create.isError ? getApiError(create.error) : null;
  const duplicateOwner =
    apiErr?.code === 'duplicate_phone'
      ? (apiErr.details?.existing as CreatedCustomer | undefined)
      : undefined;

  const altPhonesValid = altPhones.every((p) => !p || phoneLooksValid(p));
  const disabled =
    !phone ||
    !phoneLooksValid(phone) ||
    !altPhonesValid ||
    (type === 'individual' && !name) ||
    (type === 'company' && (!companyName || (!contactPerson && !name)));

  return (
    <Card
      title={isEdit ? 'Edit customer' : 'New customer'}
      subtitle={
        isEdit
          ? 'Update details — changes save to the customer record'
          : "Capture everything you'll need for invoicing and follow-ups"
      }
      action={
        <Button variant="ghost" size="sm" onClick={onCancel}>
          {cancelLabel}
        </Button>
      }
    >
      {/* A real <form autoComplete="off"> so the browser stops offering to
          "Save address / contact" every time a customer is entered — Chrome
          ignores field-level autocomplete=off but honours it on the form.
          onSubmit is neutralised; the buttons drive submission via onClick. */}
      <form className="space-y-4" autoComplete="off" onSubmit={(e) => e.preventDefault()}>
        {/* Type toggle — only when creating; type is fixed on an existing record */}
        {isEdit ? (
          <div className="text-xs text-ink-3">
            {type === 'individual' ? '👤 Individual' : '🏢 Company / Business'} customer
          </div>
        ) : (
          <div className="flex gap-2">
            {(['individual', 'company'] as CustomerType[]).map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => setType(t)}
                className={cn(
                  'flex-1 px-4 py-3 rounded border text-sm transition flex items-center justify-center gap-2',
                  type === t
                    ? 'bg-accent text-white border-accent'
                    : 'bg-paper-2 border-rule text-ink-2 hover:border-accent/40',
                )}
              >
                {t === 'individual' ? '👤 Individual' : '🏢 Company / Business'}
              </button>
            ))}
          </div>
        )}

        {type === 'company' && (
          <div className="grid grid-cols-2 gap-3">
            <Input
              label="Company name"
              required
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
              className="col-span-2"
            />
            <Input
              label="VAT number"
              value={vatNumber}
              onChange={(e) => setVatNumber(e.target.value)}
              placeholder="VAT…"
              disabled={taxIdsLocked}
              hint={taxIdsLocked ? 'Operations and above only.' : undefined}
            />
            <Input
              label="BRN"
              value={brn}
              onChange={(e) => setBrn(e.target.value)}
              placeholder="Business Registration Number"
              disabled={taxIdsLocked}
              hint={taxIdsLocked ? 'Operations and above only.' : undefined}
            />
            <Input
              label="Contact person"
              required
              value={contactPerson}
              onChange={(e) => setContactPerson(e.target.value)}
              className="col-span-2"
            />
          </div>
        )}

        {type === 'individual' && (
          <Input
            label="Customer name"
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        )}

        <div className="grid grid-cols-2 gap-3">
          <Input
            label="Phone"
            required
            value={phone}
            onChange={(e) => setPhone(sanitizePhone(e.target.value))}
            placeholder="+2305…"
            error={phoneError(phone)}
          />
          <Input
            label="Email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>

        {/* Additional numbers — one per field, never "123 / 456" in one box */}
        <div className="space-y-2">
          {altPhones.map((p, i) => (
            <div key={i} className="flex items-end gap-2">
              <div className="flex-1">
                <Input
                  label={i === 0 ? 'Additional numbers' : undefined}
                  value={p}
                  onChange={(e) =>
                    setAltPhones((prev) =>
                      prev.map((v, j) => (j === i ? sanitizePhone(e.target.value) : v)),
                    )
                  }
                  placeholder="Second GSM, landline…"
                  error={phoneError(p)}
                />
              </div>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="mb-0.5"
                onClick={() => setAltPhones((prev) => prev.filter((_, j) => j !== i))}
              >
                Remove
              </Button>
            </div>
          ))}
          {altPhones.length < 4 && (
            <button
              type="button"
              className="text-xs text-accent hover:underline"
              onClick={() => setAltPhones((prev) => [...prev, ''])}
            >
              + Add another number
            </button>
          )}
        </div>

        {/* NIC + DOB (DOB only for individuals) */}
        <div className={cn('grid gap-3', type === 'individual' ? 'grid-cols-2' : 'grid-cols-1')}>
          <Input
            label={
              type === 'company' ? 'Contact NIC / passport (optional)' : 'NIC / passport (optional)'
            }
            value={nationalId}
            onChange={(e) => setNationalId(e.target.value)}
            placeholder="National ID or passport number"
            hint="Used for verification on deliveries and high-value orders."
          />
          {type === 'individual' && (
            <Input
              label="Date of birth (optional)"
              type="date"
              value={dateOfBirth}
              onChange={(e) => setDateOfBirth(e.target.value)}
              hint="For birthday outreach + verification."
              max={new Date().toISOString().slice(0, 10)}
            />
          )}
        </div>

        {/* Address with Nominatim autocomplete */}
        <div className="grid grid-cols-3 gap-3">
          <AddressAutocomplete
            label="Address"
            value={address}
            onChange={setAddress}
            onPick={(p) => {
              if (p.city) setCity(p.city);
              setLatitude(p.latitude);
              setLongitude(p.longitude);
            }}
            placeholder="Start typing…"
            className="col-span-2"
          />
          <Input label="City / Town" value={city} onChange={(e) => setCity(e.target.value)} />
        </div>

        {/* Precise address & landmarks — used when the map can't pinpoint the spot.
            Lives on the customer; it's what prints on the invoice + delivery note. */}
        <div className="rounded-md border border-dashed border-rule p-3 space-y-1.5">
          <label className="block text-xs font-medium text-ink-3 uppercase tracking-wide">
            Precise address &amp; landmarks <span className="text-ink-4 normal-case">· optional</span>
          </label>
          <p className="text-xs text-ink-3 leading-relaxed">
            If the map can&apos;t find the exact address, pick the closest point above, then write the
            <span className="text-ink"> full precise address</span> here (house name, lane, landmark,
            directions). When set, <span className="text-ink">this</span> is what prints on the
            invoice and the driver&apos;s delivery note.
          </p>
          <textarea
            rows={3}
            className="kc-input w-full resize-y"
            placeholder="e.g. Morcellement St Jean, 3rd lane after the temple, blue gate — house of Mr Ramduth"
            value={addressPrecise}
            onChange={(e) => setAddressPrecise(e.target.value)}
          />
        </div>

        {/* Delivery routing region — auto-suggested from city */}
        <label className="flex flex-col gap-1">
          <span className="text-xs font-medium text-ink-3 uppercase tracking-wide">
            Delivery region
          </span>
          <select
            value={region}
            onChange={(e) => {
              setRegion(e.target.value as Region | '');
              setRegionAutoSuggested(false);
            }}
            className="kc-input"
          >
            <option value="">— pick a region —</option>
            <option value="center">Center · Port Louis, Curepipe, Rose Hill, Quatre Bornes…</option>
            <option value="north">
              North · Pamplemousses, Triolet, Grand Bay, Rivière du Rempart…
            </option>
            <option value="east">East · Flacq, Belle Mare, Trou d'Eau Douce…</option>
            <option value="south">South · Mahebourg, Souillac, Rose Belle…</option>
            <option value="west">
              West · Tamarin, Flic en Flac, Albion, Black River, Chamarel…
            </option>
          </select>
          <span className="text-[10px] text-ink-3 mt-0.5">
            {regionAutoSuggested
              ? '✓ Suggested from city — change if needed.'
              : 'Determines which day the delivery truck visits this customer.'}
          </span>
        </label>

        {/* Map pin — opens the LocationPicker */}
        <div className="rounded-md border border-dashed border-rule px-3 py-2 flex items-center justify-between gap-3">
          <div className="text-xs">
            {latitude != null && longitude != null ? (
              <span className="text-ink-2">
                Pinned at{' '}
                <span className="kc-mono text-ink-3">
                  {latitude.toFixed(5)}, {longitude.toFixed(5)}
                </span>
              </span>
            ) : (
              <span className="text-ink-3">No map pin — add one for accurate delivery routing</span>
            )}
          </div>
          <Button
            type="button"
            variant="tertiary"
            size="xs"
            className="whitespace-nowrap"
            onClick={() => setPickerOpen(true)}
          >
            {latitude != null ? 'Change pin' : 'Pin on map'}
          </Button>
        </div>
        <LocationPicker
          open={pickerOpen}
          onClose={() => setPickerOpen(false)}
          initial={{ lat: latitude, lng: longitude, address }}
          onConfirm={({ lat, lng, address: addr }) => {
            setLatitude(lat);
            setLongitude(lng);
            if (addr) setAddress(addr);
            setPickerOpen(false);
          }}
        />

        <label className="flex items-start gap-3 p-3 rounded border border-rule cursor-pointer hover:bg-paper-2">
          <input
            type="checkbox"
            checked={vatExcluded}
            onChange={(e) => setVatExcluded(e.target.checked)}
            className="accent-accent mt-0.5"
          />
          <div>
            <div className="text-sm text-ink">VAT excluded</div>
            <div className="text-xs text-ink-3">
              Tick this if the customer is zero-rated, exempt, or invoiced VAT-free. All orders for
              this customer will skip VAT.
            </div>
          </div>
        </label>

        {duplicateOwner ? (
          <div className="text-sm bg-warning-soft text-warning px-3 py-2.5 rounded border border-warning/20 space-y-2">
            <div>
              <div className="font-medium">This number already belongs to a customer</div>
              <div className="text-ink-2 mt-0.5">
                {duplicateOwner.type === 'company'
                  ? (duplicateOwner.companyName ?? duplicateOwner.name)
                  : duplicateOwner.name}{' '}
                <span className="kc-mono text-ink-3">· {duplicateOwner.customerCode}</span>
                {duplicateOwner.preferredShop ? ` · ${duplicateOwner.preferredShop.name}` : ''}
              </div>
              <div className="text-xs text-ink-3 kc-mono mt-0.5">{duplicateOwner.phone}</div>
            </div>
            <div className="flex gap-2">
              <Button type="button" size="xs" onClick={() => onCreated(duplicateOwner)}>
                Use this customer
              </Button>
              <Button type="button" size="xs" variant="secondary" onClick={() => create.reset()}>
                Edit number
              </Button>
            </div>
          </div>
        ) : create.isError ? (
          <div className="text-sm bg-negative-soft text-negative px-3 py-2 rounded border border-negative/20">
            {apiErr?.message}
          </div>
        ) : null}

        <div className="flex gap-2">
          <Button type="button" variant="secondary" className="flex-1" onClick={onCancel}>
            {cancelLabel}
          </Button>
          <Button
            type="button"
            className="flex-1"
            loading={create.isPending}
            disabled={disabled}
            onClick={submit}
          >
            {isEdit ? 'Save changes' : 'Create customer'}
          </Button>
        </div>
      </form>
    </Card>
  );
}
